import { z } from 'zod';
import { ulidSchema } from './id';

/**
 * Power-Ups — B72, `FR-AUTO-023` through `FR-AUTO-028`.
 *
 * These contracts describe the generic framework state only: catalogue rows,
 * enabled board rows, declared surfaces, local authorisation state, opaque data
 * lifecycle and enterprise policy. Concrete integrations execute in later
 * blocks.
 */

export const POWER_UP_CATEGORIES = [
  'analytics',
  'automation',
  'developer_tools',
  'file_management',
  'communication',
  'human_resources',
  'it',
  'marketing',
  'product_design',
  'project_management',
  'sales',
] as const;

export const POWER_UP_PRICING_MODELS = [
  'free',
  'paid',
  'requires_separate_subscription',
] as const;

export const POWER_UP_POLICY_POSTURES = ['allow_list', 'block_list'] as const;
export const POWER_UP_DATA_SCOPES = ['board', 'card', 'personal'] as const;
export const POWER_UP_PERMISSION_SCOPES = [
  'read_board',
  'read_cards',
  'read_members',
  'read_activity',
  'write_comments',
  'write_attachments',
  'write_power_up_data',
] as const;

export const POWER_UP_JSON_MAX_BYTES = 16_384;
export const POWER_UP_JSON_MAX_DEPTH = 8;
export const POWER_UP_JSON_MAX_ARRAY_LENGTH = 100;
export const POWER_UP_JSON_MAX_OBJECT_KEYS = 100;
export const POWER_UP_JSON_KEY_MAX_LENGTH = 128;
export const POWER_UP_JSON_STRING_MAX_LENGTH = 4096;
export const POWER_UP_SEARCH_MAX_LENGTH = 255;

export const powerUpCategorySchema = z.enum(POWER_UP_CATEGORIES);
export type PowerUpCategory = z.infer<typeof powerUpCategorySchema>;

export const powerUpPricingModelSchema = z.enum(POWER_UP_PRICING_MODELS);
export type PowerUpPricingModel = z.infer<typeof powerUpPricingModelSchema>;

export const powerUpPolicyPostureSchema = z.enum(POWER_UP_POLICY_POSTURES);
export type PowerUpPolicyPosture = z.infer<typeof powerUpPolicyPostureSchema>;

export const powerUpDataScopeSchema = z.enum(POWER_UP_DATA_SCOPES);
export type PowerUpDataScope = z.infer<typeof powerUpDataScopeSchema>;

export const powerUpPermissionScopeSchema = z.enum(POWER_UP_PERMISSION_SCOPES);
export type PowerUpPermissionScope = z.infer<typeof powerUpPermissionScopeSchema>;

const timestampStringSchema = z.string().min(1).max(64);
const labelSchema = z.string().trim().min(1).max(255);
const optionalUrlSchema = z.string().trim().url().max(2048).nullable();

type PowerUpJsonValueInternal =
  | string
  | number
  | boolean
  | null
  | PowerUpJsonValueInternal[]
  | { [key: string]: PowerUpJsonValueInternal };

function jsonLength(value: unknown): number {
  const encoded = JSON.stringify(value);
  return encoded === undefined ? POWER_UP_JSON_MAX_BYTES + 1 : encoded.length;
}

function jsonDepth(value: PowerUpJsonValueInternal): number {
  if (value === null || typeof value !== 'object') {
    return 0;
  }

  if (Array.isArray(value)) {
    return 1 + Math.max(0, ...value.map((item) => jsonDepth(item)));
  }

  return 1 + Math.max(0, ...Object.values(value).map((item) => jsonDepth(item)));
}

export const powerUpJsonValueSchema: z.ZodType<PowerUpJsonValueInternal> = z.lazy(() =>
  z.union([
    z.string().max(POWER_UP_JSON_STRING_MAX_LENGTH),
    z.number().finite(),
    z.boolean(),
    z.null(),
    z.array(powerUpJsonValueSchema).max(POWER_UP_JSON_MAX_ARRAY_LENGTH),
    z
      .record(z.string().min(1).max(POWER_UP_JSON_KEY_MAX_LENGTH), powerUpJsonValueSchema)
      .refine((value) => Object.keys(value).length <= POWER_UP_JSON_MAX_OBJECT_KEYS, {
        message: `Object must have at most ${POWER_UP_JSON_MAX_OBJECT_KEYS} keys`,
      }),
  ]),
);

export type PowerUpJsonValue = z.infer<typeof powerUpJsonValueSchema>;

export const powerUpJsonObjectSchema = z
  .record(z.string().min(1).max(POWER_UP_JSON_KEY_MAX_LENGTH), powerUpJsonValueSchema)
  .refine((value) => Object.keys(value).length <= POWER_UP_JSON_MAX_OBJECT_KEYS, {
    message: `Object must have at most ${POWER_UP_JSON_MAX_OBJECT_KEYS} keys`,
  })
  .superRefine((value, context) => {
    if (jsonLength(value) > POWER_UP_JSON_MAX_BYTES) {
      context.addIssue({
        code: 'custom',
        message: `JSON object must be at most ${POWER_UP_JSON_MAX_BYTES} bytes`,
      });
    }

    if (jsonDepth(value) > POWER_UP_JSON_MAX_DEPTH) {
      context.addIssue({
        code: 'custom',
        message: `JSON object depth must be at most ${POWER_UP_JSON_MAX_DEPTH}`,
      });
    }
  });

const manifestEntrySchema = z.strictObject({
  key: z.string().trim().min(1).max(64),
  label: labelSchema,
  description: z.string().trim().min(1).max(255).optional(),
  emptyState: z.string().trim().min(1).max(512).optional(),
});

export const powerUpManifestSchema = z
  .strictObject({
    cardFrontBadges: z.array(manifestEntrySchema).max(25).default([]),
    cardBackBadges: z.array(manifestEntrySchema).max(25).default([]),
    cardBackSections: z.array(manifestEntrySchema).max(25).default([]),
    cardBackButtons: z.array(manifestEntrySchema).max(25).default([]),
    boardHeaderButtons: z.array(manifestEntrySchema).max(25).default([]),
    listMenuActions: z.array(manifestEntrySchema).max(25).default([]),
    listMenuSorts: z.array(manifestEntrySchema).max(25).default([]),
    attachmentSections: z.array(manifestEntrySchema).max(25).default([]),
    attachmentThumbnails: z.array(manifestEntrySchema).max(25).default([]),
    settings: z.strictObject({ label: labelSchema }).nullable().default(null),
    authorization: z
      .strictObject({ label: labelSchema, accountLabel: labelSchema.optional() })
      .nullable()
      .default(null),
  })
  .superRefine((value, context) => {
    if (jsonLength(value) > POWER_UP_JSON_MAX_BYTES) {
      context.addIssue({
        code: 'custom',
        message: `Manifest must be at most ${POWER_UP_JSON_MAX_BYTES} bytes`,
      });
    }
  });

export type PowerUpManifest = z.infer<typeof powerUpManifestSchema>;

export const powerUpPermissionGrantSchema = z.strictObject({
  scope: powerUpPermissionScopeSchema,
  label: labelSchema,
});

export type PowerUpPermissionGrant = z.infer<typeof powerUpPermissionGrantSchema>;

export const powerUpSummarySchema = z.object({
  id: ulidSchema,
  name: z.string().min(1).max(255),
  iconUrl: optionalUrlSchema,
  publisher: z.string().min(1).max(255).nullable(),
  description: z.string().min(1).nullable(),
  category: powerUpCategorySchema,
  pricingModel: powerUpPricingModelSchema,
  madeByUs: z.boolean(),
  permissionsDisclosure: z.string().min(1).nullable(),
  permissionGrants: z.array(powerUpPermissionGrantSchema).max(50),
  requiresAuthorization: z.boolean(),
  authorizationLabel: z.string().min(1).max(255).nullable(),
});

export type PowerUpSummary = z.infer<typeof powerUpSummarySchema>;

export const powerUpDirectoryEntrySchema = powerUpSummarySchema.extend({
  enabledOnBoard: z.boolean().optional(),
});

export type PowerUpDirectoryEntry = z.infer<typeof powerUpDirectoryEntrySchema>;

const queryBooleanSchema = z.preprocess((value) => {
  if (value === true || value === 'true') {
    return true;
  }

  if (value === false || value === 'false') {
    return false;
  }

  return value;
}, z.boolean());

export const powerUpDirectoryQuerySchema = z.strictObject({
  q: z.string().trim().min(1).max(POWER_UP_SEARCH_MAX_LENGTH).optional(),
  category: powerUpCategorySchema.optional(),
  madeByUs: queryBooleanSchema.optional(),
  pricingModel: powerUpPricingModelSchema.optional(),
  boardId: ulidSchema.optional(),
});

export const powerUpDirectoryResponseSchema = z.object({
  powerUps: z.array(powerUpDirectoryEntrySchema),
});

export type PowerUpDirectoryResponse = z.infer<typeof powerUpDirectoryResponseSchema>;

export const powerUpDetailSchema = powerUpSummarySchema.extend({
  manifest: powerUpManifestSchema,
});

export type PowerUpDetail = z.infer<typeof powerUpDetailSchema>;

export const powerUpAuthorizationStateSchema = z.enum([
  'not_required',
  'connect_required',
  'authorized',
  'reconnect_required',
]);

export type PowerUpAuthorizationState = z.infer<typeof powerUpAuthorizationStateSchema>;

export const boardPowerUpSchema = z.object({
  id: ulidSchema,
  boardId: ulidSchema,
  powerUp: powerUpDetailSchema,
  settings: powerUpJsonObjectSchema.nullable(),
  headerButtonVisible: z.boolean(),
  enabledAt: timestampStringSchema,
  disabledAt: timestampStringSchema.nullable(),
  authorizationState: powerUpAuthorizationStateSchema,
});

export type BoardPowerUp = z.infer<typeof boardPowerUpSchema>;

export const boardPowerUpListSchema = z.object({
  powerUps: z.array(boardPowerUpSchema),
});

export type BoardPowerUpList = z.infer<typeof boardPowerUpListSchema>;

export const enableBoardPowerUpBodySchema = z.strictObject({
  settings: powerUpJsonObjectSchema.optional(),
});

export const boardPowerUpHeaderVisibilityBodySchema = z.strictObject({
  visible: z.boolean(),
});

export const boardPowerUpSettingsBodySchema = z.strictObject({
  settings: powerUpJsonObjectSchema.nullable(),
});

export const boardPowerUpAuthorizationBodySchema = z.strictObject({
  authorized: z.literal(true).optional(),
});

export const disableBoardPowerUpBodySchema = z.strictObject({
  keepData: z.boolean().default(false),
});

export const removePersonalPowerUpSettingsBodySchema = z.strictObject({});

export const powerUpDataSeedBodySchema = z
  .strictObject({
    scope: powerUpDataScopeSchema,
    cardId: ulidSchema.optional(),
    data: powerUpJsonObjectSchema,
  })
  .superRefine((body, context) => {
    if (body.scope === 'card' && body.cardId === undefined) {
      context.addIssue({
        code: 'custom',
        path: ['cardId'],
        message: 'cardId is required for card-scoped Power-Up data',
      });
    }

    if (body.scope !== 'card' && body.cardId !== undefined) {
      context.addIssue({
        code: 'custom',
        path: ['cardId'],
        message: 'cardId is only valid for card-scoped Power-Up data',
      });
    }
  });

export const powerUpPolicySchema = z.object({
  enterpriseId: ulidSchema,
  posture: powerUpPolicyPostureSchema,
  powerUps: z.array(powerUpSummarySchema),
});

export type PowerUpPolicy = z.infer<typeof powerUpPolicySchema>;

export const powerUpPolicyPatchSchema = z.strictObject({
  posture: powerUpPolicyPostureSchema,
  powerUpIds: z.array(ulidSchema).max(200),
});

export const powerUpUsageEntrySchema = z.object({
  powerUp: powerUpSummarySchema,
  boardCount: z.number().int().nonnegative(),
});

export const powerUpUsageResponseSchema = z.object({
  powerUps: z.array(powerUpUsageEntrySchema),
});

export type PowerUpUsageResponse = z.infer<typeof powerUpUsageResponseSchema>;

export const powerUpAffectedBoardSchema = z.object({
  id: ulidSchema,
  title: z.string().min(1).max(512),
  workspaceId: ulidSchema,
});

export const powerUpAffectedBoardsResponseSchema = z.object({
  boards: z.array(powerUpAffectedBoardSchema),
});

export type PowerUpAffectedBoardsResponse = z.infer<
  typeof powerUpAffectedBoardsResponseSchema
>;

export const bulkDisablePowerUpBodySchema = disableBoardPowerUpBodySchema;

export const bulkDisablePowerUpResponseSchema = z.object({
  disabledBoardPowerUpIds: z.array(ulidSchema),
});

export type BulkDisablePowerUpResponse = z.infer<typeof bulkDisablePowerUpResponseSchema>;
